import { Component, Input, Output, EventEmitter, HostBinding, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateTime } from 'luxon';

export interface WeekDay {
  day: string;
  date: number;
  count: number;
  isSelected: boolean;
  isCurrentDay: boolean;
}

interface EncounterData {
  count: number;
  label: string;
  color: string;
  bg: string;
  /** When set, displayed instead of count (e.g. "5h 7m") */
  displayText?: string;
  encounters?: { encounterId: string; patientId: string }[];
}
interface ScheduleData {
  count: number;
  label: string;
  color: string;
  bg: string;
}

interface VisitData {
  count: number;
  label: string;
  color: string;
  bg: string;
  encounters?: { encounterId: string; patientId: string }[];
}

interface OrdersData {
  count: number;
  label: string;
  color: string;
  bg: string;
}

interface EngageData {
  count: number;
  label: string;
  color: string;
  bg: string;
}

interface TodayScheduleData {
  dos: string;
  name: string;
  provider: string;
  status: string;
  location: string;
  eligibility: string;
  action: string;
}

/** Engage Patient list (`/patients`) sidebar activity filter — set on dashboard notification tiles for deep links. */
export type EngagePatientsActivityFilter =
  | 'all'
  | 'new'
  | 'unread_messages'
  | 'voicemail'
  | 'reminders'
  | 'missed_call';

export interface NotificationItem {
  value: number;
  label: string;
  color: string;
  bg: string;
  clickable?: boolean;
  /** When set, Dashboard can navigate to Patients and apply this sidebar filter (e.g. missed calls). */
  engageActivityFilter?: EngagePatientsActivityFilter;
}

export interface ProspectMetricItem {
  label: string;
  count: number;
  color: string;
  bg: string;
  encounters?: { encounterId: string; patientId: string }[];
  /** Referrals tile: from Engage `GET …/dashboard/prospect` `total_leads_list`. */
  patientIds?: string[];
}

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss']
})
export class CardComponent implements OnChanges {
  /** Prevent native tooltip on host (e.g. "Prospects" from title input). */
  @HostBinding('attr.title') hostTitle: string | null = null;
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() customClass: string = '';
  @Input() encounterContent: boolean = false;
  @Input() scheduleContent: boolean = false;
  @Input() prospectContent: boolean = false;
  @Input() ordersContent: boolean = false;
  @Input() engageContent: boolean = false;
  @Input() notificationContent: boolean = false;
  @Input() tableContent: boolean = false;
  @Input() visitContent: boolean = false;
  /** Week days with encounter counts from API (for Upcoming Visits) */
  @Input() upcomingWeekDays: WeekDay[] | null = null;
  /** Stats (New, Fol. Up, Diag, Other) from API - when set, overrides internal visitData */
  @Input() visitStats: VisitData[] | null = null;
  /** Encounter status counts from API (Open, Sign Req, Unsubmitted) - when set, overrides internal encounterData */
  @Input() encounterStats: EncounterData[] | null = null;
  /** Notification list items (icon, value, label) - when set, used for notificationContent */
  @Input() notificationItems: NotificationItem[] | null = null;
  /** When true, show skeleton loaders in each notification count card */
  @Input() notificationLoading: boolean = false;
  /** When true, hide the date range dropdown (e.g. when range is controlled at page level) */
  @Input() hideNotificationRangeFilter: boolean = false;
  /** When set, used as the selected value for the notification range dropdown (e.g. from dashboard); default 'all' */
  @Input() notificationRangeValue: 'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week' = 'all';
  /** When true, show skeleton loaders in encounter content (e.g. while refetching after filter change) */
  @Input() encounterLoading: boolean = false;
  /** Lead Time section: First Response, Appt. Lead Time (uses same card style as encounter, with displayText) */
  @Input() leadTimeContent: boolean = false;
  /** Lead Time stats from API - when set, overrides default leadTimeData */
  @Input() leadTimeStats: EncounterData[] | null = null;
  @Input() leadTimeLoading: boolean = false;
  /** When true, show skeleton loaders in visit stats (Upcoming Visits cards below week strip) */
  @Input() visitLoading: boolean = false;
  /** Prospect metrics: Leads, Prospects, No Show, Established - when set, overrides default prospectMetrics */
  @Input() prospectMetrics: ProspectMetricItem[] | null = null;
  /** When true, show skeleton loaders in prospect content */
  @Input() prospectLoading: boolean = false;
  /** Total count displayed on top right of Prospect section */
  @Input() prospectTotalCount: number | null = null;
  @Output() weekChanged = new EventEmitter<DateTime>();
  @Output() dateSelected = new EventEmitter<DateTime>();
  @Output() notificationItemClick = new EventEmitter<NotificationItem>();
  /** Emits when the notification date range dropdown changes: 'today' | 7 | 14 | 30 */
  @Output() notificationRangeChange = new EventEmitter<'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week'>();
  /** Emits when an encounter stat card (Open, Sign Req, Unsubmitted) is clicked - for navigation with filter */
  @Output() encounterItemClick = new EventEmitter<EncounterData>();
  /** Emits when an Upcoming Visits stat card (Clinic, Telehealth, …) is clicked */
  @Output() visitItemClick = new EventEmitter<VisitData>();
  /** Emits when a Prospects stat card (Booked, No Show, Established) is clicked */
  @Output() prospectItemClick = new EventEmitter<ProspectMetricItem>();
  encounterData: EncounterData[] = [
    {
      count: 8,
      label: 'Open',
      color: 'danger',
      bg:'bg-soft-danger'
    },
    {
      count: 29,
      label: 'Sign Req',
      color: 'warning',
      bg:'bg-soft-warning'
    },
    {
      count: 17,
      label: 'Unsubmitted',
      color: 'muted',
      bg:'bg-soft-gray'
    }
  ];
  leadTimeData: EncounterData[] = [
    { count: 0, label: 'First Response', color: 'info', bg: 'bg-soft-info', displayText: '—' },
    { count: 0, label: 'Appt. Lead Time', color: 'success', bg: 'bg-soft-success', displayText: '—' },
  ];
  prospectMetricsData: ProspectMetricItem[] = [
    { label: 'Referrals', count: 10, color: 'primary', bg: 'bg-soft-primary' },
    { label: 'Booked', count: 0, color: 'success', bg: 'bg-soft-success' },
    { label: 'No Show', count: 38, color: 'warning', bg: 'bg-soft-warning' },
    { label: 'Established', count: 45, color: 'muted', bg: 'bg-soft-gray' },
  ];
  scheduleData: ScheduleData[] = [
    {
      count: 17,
      label: 'Requested',
      color: 'muted',
      bg:'bg-soft-gray'
    },
    {
      count: 38,
      label: 'Eligibility',
      color: 'warning',
      bg:'bg-soft-warning'
    },
    {
      count: 29,
      label: 'Pending Conf',
      color: 'success',
      bg:'bg-soft-success'
    }
  ];
  visitData: VisitData[] = [
    {
      count: 7,
      label: 'Clinic',
      color: 'muted',
      bg:'bg-soft-gray'
    },
    {
      count: 15,
      label: 'Telehealth',
      color: 'muted',
      bg:'bg-soft-gray'
    },
    {
      count: 5,
      label: 'Procedures',
      color: 'muted',
      bg:'bg-soft-gray'
    },
    {
      count: 9,
      label: 'Other',
      color: 'muted',
      bg:'bg-soft-gray'
    }
  ];
  ordersData: OrdersData[] = [
    {
      count: 46,
      label: 'New',
      color: 'primary',
      bg:'bg-soft-primary'
    },
    {
      count: 67,
      label: 'Processed',
      color: 'muted',
      bg:'bg-soft-gray'
    },
    {
      count: 18,
      label: 'Fulfilled',
      color: 'info',
      bg:'bg-soft-info'
    },
    {
      count: 14,
      label: 'Pending Rev',
      color: 'warning',
      bg:'bg-soft-warning'
    }
  ];
  engageData: EngageData[] = [
    {
      count: 5,
      label: 'New Messages',
      color: 'primary',
      bg:'bg-soft-primary'
    },
    {
      count: 29,
      label: 'Follow-up Req.',
      color: 'warning',
      bg:'bg-soft-warning'
    },
    {
      count: 8,
      label: 'Response Pending',
      color: 'danger',
      bg:'bg-soft-danger'
    }
  ];
  notificationRangeOptions: { label: string; value: 'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: '14 Days', value: 14 },
    { label: '30 Days', value: 30 },
  ];
  /** FormControl for the notification period dropdown (synced with notificationRangeValue). */
  notificationRangeControl = new FormControl<'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week'>('all');
  selectedNotificationRange: 'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week' = 'all';
  notificationData: NotificationItem[] = [
    { value: 19, label: 'New Referrals', color: 'primary', bg: 'bg-soft-primary', clickable: true },
    { value: 0, label: 'Unread Messages', color: 'info', bg: 'bg-soft-info', clickable: true },
    { value: 0, label: 'Missed Calls', color: 'warning', bg: 'bg-soft-warning', clickable: true },
    { value: 0, label: 'New Voicemail', color: 'danger', bg: 'bg-soft-danger', clickable: true },
    { value: 0, label: 'Received Fax', color: 'muted', bg: 'bg-soft-gray', clickable: false },
    { value: 2, label: 'Reminders', color: 'success', bg: 'bg-soft-success', clickable: true },
  ];
  todayScheduleData: TodayScheduleData[] = [
    {
      dos: '8/7/2025 8:00 AM',
      name: 'John Doe',
      provider: 'Dr. John Doe',
      status: 'New Patient',
      location: 'Clinic Visit',
      eligibility: 'Authorized',
      action: 'Checked-in'
    },
    {
      dos: '8/9/2025 9:00 AM',
      name: 'Jane Doe',
      provider: 'Dr. Jane Doe',
      status: 'New Patient',
      location: 'Clinic Visit',
      eligibility: 'Pending',
      action: 'Confirm App'
    },
    {
      dos: '8/11/2025 11:00 AM',
      name: 'Jim Doe',
      provider: 'Dr. Jim Doe',
      status: 'New Patient',
      location: 'Clinic Visit',
      eligibility: 'Authorized',
      action: 'Check-in'
    },
    {
      dos:' 8/14/2025 12:30 PM',
      name: 'Jill Doe',
      provider: 'Dr. Jill Doe',
      status: 'New Patient',
      location: 'Clinic Visit',
      eligibility: 'Not Authorized ',
      action: 'Confirm App'
    },
    {
      dos: '8/14/2025 1:15 PM',
      name: 'Jill Doe',
      provider: 'Dr. Jill Doe',
      status: 'New Patient',
      location: 'Clinic Visit',
      eligibility: 'Pending',
      action: 'check-in'
    }
  ];
  isCalendarOpen: boolean = false;

  onCalendarStateChanged(isOpen: boolean) {
    this.isCalendarOpen = isOpen;
  }

  onDayCountSelected(dayCount: number) {
    if (this.visitStats != null) {
      return;
    }
    this.distributeVisitCounts(dayCount);
  }

  get effectiveVisitData(): VisitData[] {
    return this.visitStats ?? this.visitData;
  }

  get effectiveEncounterData(): EncounterData[] {
    return this.encounterStats ?? this.encounterData;
  }

  get effectiveLeadTimeData(): EncounterData[] {
    return this.leadTimeStats ?? this.leadTimeData;
  }

  get effectiveNotificationItems(): NotificationItem[] {
    return this.notificationItems ?? this.notificationData;
  }

  get effectiveProspectMetrics(): ProspectMetricItem[] {
    return this.prospectMetrics ?? this.prospectMetricsData;
  }

  onNotificationItemClick(item: NotificationItem): void {
    if (item.clickable !== false) {
      this.notificationItemClick.emit(item);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notificationRangeValue'] && this.notificationRangeValue !== this.notificationRangeControl.value) {
      this.notificationRangeControl.setValue(this.notificationRangeValue, { emitEvent: false });
    }
  }

  onNotificationRangeChange(event: { value: 'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week' }): void {
    if (event?.value != null) {
      this.notificationRangeChange.emit(event.value);
    }
  }

  /** Handle app-dropdown change for notification period (Today, This/Last Week, 7/14/30 Days). */
  onNotificationRangeSelect(value: 'today' | 'all' | 7 | 14 | 30 | 'this_week' | 'last_week'): void {
    if (value !== undefined && value !== null) {
      this.notificationRangeChange.emit(value);
    }
  }

  /** Encounters widget stat tiles — clickable when count > 0 (dashboard → worklist patient filter). */
  isEncounterItemClickable(item: EncounterData): boolean {
    return (item?.count ?? 0) > 0;
  }

  isVisitItemClickable(item: VisitData): boolean {
    return (item?.count ?? 0) > 0;
  }

  /** Prospects widget — Referrals (patient id list) or Booked / No Show / Established when count > 0. */
  isProspectItemClickable(item: ProspectMetricItem): boolean {
    if ((item?.count ?? 0) <= 0) {
      return false;
    }
    const label = (item?.label ?? "").trim();
    if (label === "Referrals") {
      return (item.patientIds?.length ?? 0) > 0;
    }
    return label === "Booked" || label === "No Show" || label === "Established";
  }

  private distributeVisitCounts(totalCount: number) {
    const distributionRatios = [0.19, 0.42, 0.14, 0.25];
    
    this.visitData.forEach((item, index) => {
      if (index < distributionRatios.length) {
        const distributedCount = Math.round(totalCount * distributionRatios[index]);
        item.count = distributedCount;
      }
    });
    
    const currentTotal = this.visitData.reduce((sum, item) => sum + item.count, 0);
    if (currentTotal !== totalCount) {
      const lastIndex = this.visitData.length - 1;
      this.visitData[lastIndex].count += (totalCount - currentTotal);
    }
  }
}
